"use client";

import { itemFromProductDetail, trackSelectVariant, trackViewItem } from "@v2/lib/analytics";
import { useEffect, useRef } from "react";
import { usePdpVariant } from "./PdpVariantContext";

/**
 * Reports what is being looked at on the product page.
 *
 * The first variant the page settles on is the view_item. Switching colour or
 * size afterwards is a select_variant, not another view_item — the shopper is
 * still on the same page, and counting each swatch as a fresh product view
 * would make this one page look like five.
 *
 * It waits for mounted, because the provider resolves the variant from the URL
 * in a layout effect and firing before that would report the wrong colour.
 */
export default function PdpAnalytics({ brandName, siteProductId }: {
    brandName?: string | null;
    siteProductId: number;
}) {
    const { showProduct, mounted } = usePdpVariant();
    const reportedVariant = useRef<number | null>(null);

    useEffect(() => {
        if (!mounted || !showProduct) return;

        const variantId = showProduct.product_id;
        if (reportedVariant.current === variantId) return;

        const item = itemFromProductDetail(showProduct, {
            brand: brandName,
            siteProductId,
        });

        if (reportedVariant.current === null) {
            trackViewItem(item);
        } else {
            trackSelectVariant(item);
        }

        reportedVariant.current = variantId;
    }, [mounted, showProduct, brandName, siteProductId]);

    return null;
}
